import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { RefItem, ResourceName } from '@bingo/shared';
import { api } from './api';

type Lists = Record<ResourceName, RefItem[]>;

interface RefState {
  lists: Lists | null;
  error: string;
  reload: () => Promise<void>;
  /** Replaces one item after a save, without reloading everything. */
  put: (resource: ResourceName, item: RefItem) => void;
  byId: (resource: ResourceName, id: unknown) => RefItem | undefined;
}

const Ctx = createContext<RefState | null>(null);

export function RefProvider({ children }: { children: ReactNode }) {
  const [lists, setLists] = useState<Lists | null>(null);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    try {
      setLists(await api<Lists>('/api/ref'));
      setError('');
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => void reload(), [reload]);

  const put = useCallback((resource: ResourceName, item: RefItem) => {
    setLists((prev) => {
      if (!prev) return prev;
      const list = prev[resource];
      const i = list.findIndex((x) => x.id === item.id);
      const next = i >= 0 ? list.map((x) => (x.id === item.id ? item : x)) : [item, ...list];
      return { ...prev, [resource]: next };
    });
  }, []);

  const byId = useCallback(
    (resource: ResourceName, id: unknown) => (id ? lists?.[resource].find((x) => x.id === id) : undefined),
    [lists],
  );

  return <Ctx.Provider value={{ lists, error, reload, put, byId }}>{children}</Ctx.Provider>;
}

export function useRefData(): RefState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useRefData outside RefProvider');
  return v;
}
